import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Cloud,
  Sun,
  CloudRain,
  CloudSnow,
  CloudLightning,
  CloudFog,
  Loader2,
  Thermometer,
  Droplets,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ICONS = {
  sunny: Sun,
  cloudy: Cloud,
  rainy: CloudRain,
  snowy: CloudSnow,
  stormy: CloudLightning,
  fog: CloudFog,
};

const ICON_COLORS = {
  sunny: "text-amber-400",
  cloudy: "text-zinc-400",
  rainy: "text-sky-400",
  snowy: "text-blue-300",
  stormy: "text-purple-400",
  fog: "text-zinc-500",
};

const BG_COLORS = {
  sunny: "from-amber-500/10 to-orange-500/5",
  cloudy: "from-zinc-500/10 to-slate-500/5",
  rainy: "from-sky-500/10 to-blue-500/5",
  snowy: "from-blue-300/10 to-indigo-500/5",
  stormy: "from-purple-500/10 to-violet-500/5",
  fog: "from-zinc-400/10 to-slate-500/5",
};

export const WeatherWidget = ({ countryCode }) => {
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (countryCode) fetchWeather();
  }, [countryCode]);

  const fetchWeather = async () => {
    try {
      const res = await axios.get(`${API}/weather/${countryCode}`);
      setForecast(res.data.forecast || []);
    } catch {
      setForecast([]);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-6 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-sky-400" />
      </div>
    );
  }

  if (forecast.length === 0) return null;

  return (
    <div className="glass-card p-5" data-testid="weather-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <Thermometer className="w-5 h-5 text-sky-400" />
          7-Day Weather Forecast
        </h3>
        <div className="flex gap-1">
          <button
            onClick={() => scroll(-1)}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="weather-scroll-left"
          >
            <ChevronLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <button
            onClick={() => scroll(1)}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="weather-scroll-right"
          >
            <ChevronRight className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {forecast.map((day) => {
          const IconComp = ICONS[day.icon] || Cloud;
          const iconColor = ICON_COLORS[day.icon] || "text-zinc-400";
          const bgGrad = BG_COLORS[day.icon] || "from-zinc-500/10 to-slate-500/5";
          const dayName = new Date(day.date + "T00:00:00").toLocaleDateString("en", { weekday: "short" });
          const monthDay = new Date(day.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric" });

          return (
            <div
              key={day.date}
              className={`flex-shrink-0 w-[140px] snap-start flex flex-col items-center p-4 rounded-xl bg-gradient-to-b ${bgGrad} border border-white/5 hover:border-white/10 transition-all`}
              data-testid={`weather-day-${day.date}`}
            >
              <span className="text-sm font-semibold text-white">{dayName}</span>
              <span className="text-xs text-zinc-500 mb-3">{monthDay}</span>

              <IconComp className={`w-10 h-10 ${iconColor} mb-3`} />

              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-2xl font-bold text-white">{Math.round(day.temp_max)}°</span>
                <span className="text-sm text-zinc-500">{Math.round(day.temp_min)}°</span>
              </div>

              {day.precipitation > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Droplets className="w-3.5 h-3.5 text-sky-500" />
                  <span className="text-xs text-sky-400 font-medium">{day.precipitation}mm</span>
                </div>
              )}

              <span className="text-xs text-zinc-400 mt-2 text-center leading-tight font-medium">
                {day.description}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
